import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Brain, Calendar, LineChart, Star } from 'lucide-react';
import './SetupExperience.css';

const STEPS = [
  { icon: Sparkles,  label: 'Setting up your personalised dashboard' },
  { icon: Brain,     label: 'Analysing your habits and routines' },
  { icon: Calendar,  label: 'Building your custom weekly plan' },
  { icon: LineChart, label: 'Preparing intelligent insights' },
  { icon: Star,      label: 'Finalising your Hourtrix experience' },
];

const MIN_DURATION_MS = 7200; // Minimum 7.2s, feels premium & deliberate

/**
 * Premium post-onboarding setup experience.
 * Shows animated progress with rotating messages for a minimum of 7s.
 * Calls onComplete when both minimum duration elapsed AND `ready` becomes true.
 *
 * Props:
 *   ready       - boolean, whether backend setup is actually done
 *   onComplete  - callback invoked once duration + ready conditions are met
 *   coachName   - optional string, personalises the final message
 */
function SetupExperience({ ready = true, onComplete, coachName }) {
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const startedAt = useRef(Date.now());
  const completedRef = useRef(false);

  // Smoothly animate progress toward 100% over MIN_DURATION
  useEffect(() => {
    let raf;
    const tick = () => {
      const elapsed = Date.now() - startedAt.current;
      // Progress ease: goes to 92% over duration, finishes at 100% when ready+done
      const base = Math.min(92, (elapsed / MIN_DURATION_MS) * 92);
      setProgress(base);

      // Update step index roughly every duration/steps.length
      const idx = Math.min(STEPS.length - 1, Math.floor(elapsed / (MIN_DURATION_MS / STEPS.length)));
      setStepIndex(idx);

      if (elapsed < MIN_DURATION_MS) {
        raf = requestAnimationFrame(tick);
      } else {
        setStepIndex(STEPS.length - 1);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Complete once BOTH min duration elapsed AND ready flag is true
  useEffect(() => {
    if (completedRef.current) return;
    const elapsed = Date.now() - startedAt.current;
    const remaining = Math.max(0, MIN_DURATION_MS - elapsed);

    const finish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      // Final sweep to 100% then call onComplete after a short flourish
      setProgress(100);
      setTimeout(() => onComplete && onComplete(), 500);
    };

    if (ready) {
      const t = setTimeout(finish, remaining);
      return () => clearTimeout(t);
    }
  }, [ready, onComplete]);

  const displayCoach = (coachName || '').trim() || 'your AI coach';
  const CurrentIcon = STEPS[stepIndex].icon;

  return (
    <div className="setup-experience" data-testid="setup-experience">
      <div className="setup-ambient" aria-hidden="true">
        <div className="setup-glow setup-glow-a" />
        <div className="setup-glow setup-glow-b" />
        <div className="setup-orbit">
          <span className="setup-particle" style={{ ['--i']: 0 }} />
          <span className="setup-particle" style={{ ['--i']: 1 }} />
          <span className="setup-particle" style={{ ['--i']: 2 }} />
          <span className="setup-particle" style={{ ['--i']: 3 }} />
          <span className="setup-particle" style={{ ['--i']: 4 }} />
          <span className="setup-particle" style={{ ['--i']: 5 }} />
        </div>
      </div>

      <div className="setup-stage">
        {/* Central animated emblem */}
        <div className="setup-emblem" aria-hidden="true">
          <svg className="setup-ring" viewBox="0 0 160 160">
            <defs>
              <linearGradient id="setupGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%"   stopColor="#F3DFA8" />
                <stop offset="50%"  stopColor="#D4A24C" />
                <stop offset="100%" stopColor="#A46D22" />
              </linearGradient>
            </defs>
            <circle className="setup-ring-track" cx="80" cy="80" r="70" />
            <circle
              className="setup-ring-fill"
              cx="80" cy="80" r="70"
              strokeDasharray={2 * Math.PI * 70}
              strokeDashoffset={(2 * Math.PI * 70) * (1 - progress / 100)}
            />
          </svg>
          <div className="setup-emblem-core">
            <CurrentIcon size={28} strokeWidth={1.8} />
          </div>
        </div>

        {/* Progress % */}
        <div className="setup-percent" data-testid="setup-percent">
          {Math.round(progress)}%
        </div>

        {/* Heading */}
        <h1 className="setup-title">Personalising Hourtrix</h1>
        <p className="setup-lead">
          We're tuning {displayCoach} to how <em>you</em> actually live — this only happens once.
        </p>

        {/* Step checklist */}
        <ul className="setup-steps" role="list">
          {STEPS.map((s, i) => {
            const state = i < stepIndex ? 'done' : i === stepIndex ? 'active' : 'upcoming';
            const Icon = s.icon;
            return (
              <li key={i} className={`setup-step is-${state}`} data-testid={`setup-step-${i}`}>
                <span className="setup-step-ico">
                  <Icon size={14} strokeWidth={2.2} />
                </span>
                <span className="setup-step-label">{s.label}</span>
                <span className="setup-step-status" aria-hidden="true">
                  {state === 'done' ? '✓' : state === 'active' ? <span className="ob-step-active-dot" /> : <span className="ob-step-upcoming-dot">·</span>}
                </span>
              </li>
            );
          })}
        </ul>

        {/* Reassurance */}
        <p className="setup-foot">
          Your 7-day free trial starts the moment we're done. <strong>No card required.</strong>
        </p>
      </div>
    </div>
  );
}

export default SetupExperience;
